import React, { useEffect, memo, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { HvEmptyState } from '@hitachivantara/uikit-react-core';
import { Icon } from '@iq/react-components';

import { SeverityCell } from '../../AlarmCells';
import LabelWithTooltip from '../../../../LabelWithTooltip';
import AlarmTable from './AlarmTable';

import './SourceEventsTab.scss';
import Loader from '../../../../Loader';
import {
  fetchAlarmSourceEvents,
  getSourceEventsByAlarmId,
  getSourceEventsLoading,
} from '../../../../../bundles/alarms';
import { getTimezone } from '../../../../../bundles/application';
import { formatDate, getSeverityColor } from '../../utils';

const SourceEventsTab = memo(({ alarm }) => {
  const dispatch = useDispatch();
  const timezone = useSelector(getTimezone);
  const alarmId = alarm?.id;
  const sourceEventsData = useSelector((state) => getSourceEventsByAlarmId(state, alarmId));
  const isLoading = useSelector((state) => getSourceEventsLoading(state, alarmId));

  useEffect(() => {
    if (alarmId && !sourceEventsData) {
      dispatch(fetchAlarmSourceEvents(alarm));
    }
  }, [alarmId, sourceEventsData, dispatch, alarm]);

  const events = sourceEventsData?.events || [];

  const columns = useMemo(
    () => [
      {
        Header: 'Occurred At',
        accessor: 'timestamp',
        Cell: ({ row }) => formatDate(row.original.timestamp || row.original.time, timezone),
        width: 140,
        minWidth: 140,
        maxWidth: 140,
      },
      {
        Header: 'Severity',
        accessor: 'originalSeverity',
        Cell: ({ row }) => (
          <SeverityCell
            severity={row.original.originalSeverity}
            color={getSeverityColor(row.original.originalSeverity)}
          />
        ),
        width: 90,
        minWidth: 90,
        maxWidth: 90,
      },
      {
        Header: 'Severity Text',
        accessor: 'text',
        Cell: ({ row }) => {
          const text = row.original.text || '-';
          return <LabelWithTooltip name={text} />;
        },
        minWidth: 100,
      },
    ],
    [timezone]
  );

  if (isLoading) {
    return (
      <div className="source-events-tab source-events-tab--loading">
        <Loader />
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="source-events-tab source-events-tab--empty">
        <HvEmptyState
          title="No source events found"
          message="No DTMP events were found for this alarm within the occurrence window."
          icon={
            <Icon
              icon="he-info"
              size="l"
            />
          }
        />
      </div>
    );
  }

  return (
    <div className="source-events-tab">
      <div className="source-events-header">
        <span>Aggregated DTMP events during first–last occurrence window</span>
      </div>
      <AlarmTable
        columns={columns}
        data={events}
        classPrefix="source-events"
      />
    </div>
  );
});

SourceEventsTab.displayName = 'SourceEventsTab';

export default SourceEventsTab;
